import React, { useRef, useState } from 'react'
import { Alert, Button, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import GoogleIcon from '@mui/icons-material/Google'
import Parse from 'parse'
import { MAX_PROFILE_AVATAR_MB, PROFILE_PHOTO_ACCEPT, profilePhotoError } from '../../constants/profile'
import { UploadInput } from '../styles/ProfileEditDialog.styles'

/** What the person did with their photo. A picked file is not uploaded; the owning form does that. */
export type AvatarChange =
  | { source: 'file'; file: File }
  | { source: 'google'; url: string }
  | { source: 'removed' }

interface AvatarUploadFieldProps {
  userId: string
  /** The photo to preview (a stored URL, or a local preview of a picked file), or null for none. */
  value: string | null
  onChange: (change: AvatarChange) => void
  /** Offer "Use Google photo". Only for accounts linked to Google, which can fetch it. */
  canUseGooglePhoto?: boolean
  /** The owning form is uploading or saving; the photo can't change meanwhile. */
  disabled?: boolean
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message
  }
  return fallback
}

/** Pulling the photo fails mostly on an expired Google token; say what to do about it. */
function googlePhotoError(error: unknown): string {
  const message = errorMessage(error, '')
  if (/sign in with google again|expired|token/i.test(message)) {
    return 'Your Google sign-in has expired. Sign in with Google again to use your Google photo.'
  }
  return message || 'Could not load your Google photo. Try again.'
}

/**
 * Photo picker for the onboarding "Your Sevv self" step: a round preview you can click, with
 * full-width buttons under it. It checks the picked file and hands it over; the step uploads it
 * only when the person continues, so a photo they replace or abandon never reaches storage.
 */
const AvatarUploadField: React.FC<AvatarUploadFieldProps> = ({
  userId,
  value,
  onChange,
  canUseGooglePhoto = false,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [pullingGoogle, setPullingGoogle] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Cleared so picking the same file again after a remove still fires a change.
    event.target.value = ''
    if (!file) return
    const invalid = profilePhotoError(file)
    if (invalid) {
      setError(invalid)
      return
    }
    setError(null)
    onChange({ source: 'file', file })
  }

  const handleGooglePhoto = async () => {
    setPullingGoogle(true)
    setError(null)
    try {
      const response = await Parse.Cloud.run('pullGoogleAvatar', { userId })
      if (!response?.success || !response.avatarUrl) {
        throw new Error(response?.error || 'Could not load your Google photo.')
      }
      onChange({ source: 'google', url: response.avatarUrl })
    } catch (err) {
      setError(googlePhotoError(err))
    } finally {
      setPullingGoogle(false)
    }
  }

  const handleRemove = () => {
    setError(null)
    onChange({ source: 'removed' })
  }

  const pick = () => inputRef.current?.click()
  const locked = disabled || pullingGoogle

  return (
    <Stack spacing={1.5} alignItems="stretch">
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}
      <ButtonBase
        onClick={pick}
        disabled={locked}
        aria-label={value ? 'Replace photo' : 'Upload photo'}
        sx={{
          alignSelf: 'center',
          width: 120,
          height: 120,
          mb: 1.5,
          borderRadius: '50%',
          overflow: 'hidden',
          border: 2,
          borderColor: 'primary.main',
          color: 'text.secondary',
          bgcolor: 'action.hover',
          '& img': { width: '100%', height: '100%', objectFit: 'cover' },
        }}
      >
        {value ? <img src={value} alt="Your photo" key={value} /> : <AddIcon fontSize="large" />}
      </ButtonBase>
      <UploadInput
        type="file"
        ref={inputRef}
        accept={PROFILE_PHOTO_ACCEPT}
        onChange={handleFile}
        data-testid="avatar-file-input"
      />
      <Button
        variant="outlined"
        color="inherit"
        fullWidth
        startIcon={<CloudUploadIcon />}
        onClick={pick}
        disabled={locked}
        sx={{ borderColor: 'divider', textTransform: 'none', py: 1.25 }}
      >
        {value ? 'Replace photo' : 'Upload photo'}
      </Button>
      {canUseGooglePhoto && (
        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          startIcon={pullingGoogle ? <CircularProgress size={16} /> : <GoogleIcon />}
          onClick={handleGooglePhoto}
          disabled={locked}
          sx={{ borderColor: 'divider', textTransform: 'none', py: 1.25 }}
        >
          {pullingGoogle ? 'Loading Google photo...' : 'Use Google photo'}
        </Button>
      )}
      {value && (
        <Button
          color="inherit"
          onClick={handleRemove}
          disabled={locked}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Remove photo
        </Button>
      )}
      <Typography variant="caption" color="text.secondary" align="center">
        Maximum file size: {MAX_PROFILE_AVATAR_MB} MB
      </Typography>
    </Stack>
  )
}

export default AvatarUploadField
